
import {
  computed,
  defineComponent, onBeforeMount, reactive,
  watch,
} from 'vue';
import { Modal } from 'ant-design-vue';
import { EPermissaoDados } from '@/models/Enumeradores/MeuSistema/Usuarios/EPermissaoDados';
import { ECustomRenderRow } from '@/core/models/AntDesign/IColumn';
import { IOrdemProducaoProdutoAcabado } from '@/models/Entidades/PCP/IOrdemProducao';
import { IApontamentoProdutoAcabado, IApontamentoProdutoAcabadoEstoque } from '@/models/Entidades/PCP/IApontamentoProducao';
import MensagemSemDados from '@/core/components/Tela/MensagemSemDados.vue';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import SelecionarEstoque from '../Cadastros/Estoques/SelecionarEstoque.vue';
import ServicoEstoque from '@/servicos/Cadastros/Estoques/ServicoEstoque';
import { IEstoqueItem } from '@/models/Entidades/Cadastros/Estoques/IEstoqueItem';
import { IEstoque } from '@/models/Entidades/Cadastros/Estoques/IEstoque';
import CampoNumerico from '@/core/components/Tela/CampoNumerico.vue';
import { ETipoRastreabilidade } from '@/models/Enumeradores/Estoques/Rastreabilidade/ETipoRastreabilidade';
import SelecionarUnidadeProdutoMovimento from '@/components/Cadastros/Produtos/Unidades/SelecionarUnidadeProdutoMovimento.vue';
import SelecionarProduto from '@/components/Cadastros/Produtos/SelecionarProduto.vue';
import ServicoProduto from '@/servicos/Cadastros/Produtos/ServicoProduto';
import { ETipoLancamentoRastreabilidade } from '@/models/Enumeradores/Estoques/Rastreabilidade/ETipoLancamentoRastreabilidade';
import { ETipoEtiqueta } from '@/models/Enumeradores/ETipoEtiqueta';
import ServicoOrdemProducao from '@/servicos/PCP/ServicoOrdemProducao';
import Icone from '@/core/components/Icone.vue';
import { EStatusRetornoRequisicao } from '@/core/models/IRetornoRequisicao';
import { IArquivoPdf } from '@/core/models/IArquivoPdf';
import PreviewPdf from '@/core/components/Modal/PreviewPdf.vue';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';

export default defineComponent({
  name: 'ProdutoAcabadoEtapaOrdemProducao',
  props: {
    visivel: {
      type: Boolean,
    },
    empresas: {
      type: Array as () => number[],
      required: true,
    },
    densidade: {
      type: Number,
      required: true,
    },
    produtoAcabado: {
      type: Array as () => IOrdemProducaoProdutoAcabado[],
      required: true,
    },
    codigoEtapa: {
      type: Number,
      required: true,
    },
    carregando: {
      type: Boolean,
      default: false,
    },
    editavel: {
      type: Boolean,
      default: true,
    },
    criarApontamento: {
      type: Boolean,
      required: true,
    },
    codigoProdutoOrdemProducao: {
      type: Number,
      default: 0,
    },
    casasDecimaisCusto: {
      type: Number,
      default: 2,
    },
  },
  components: {
    SelecionarEstoque,
    MensagemSemDados,
    CampoNumerico,
    SelecionarProduto,
    SelecionarUnidadeProdutoMovimento,
    Icone,
    PreviewPdf,
  },
  emits: ['salvar', 'update:visivel', 'update:carregando', 'update:produtoAcabado', 'update:criarApontamento'],
  setup(props, { emit }) {
    const servicoProducao = new ServicoOrdemProducao();
    const servicoEstoque = new ServicoEstoque();
    const servicoProduto = new ServicoProduto();
    servicoEstoque.requisicaoSistema();

    const state = reactive({
      produtoAcabado: [] as IOrdemProducaoProdutoAcabado[],
      editavel: true,
      etapaSelecionada: 0,
      codigoProdutoSelecionado: -1,
      codigoProduto: 0,
      codigoUnidade: 0,
      quantidade: 0,
      estoqueItem: {} as IEstoqueItem,
      estoque: {} as IEstoque,
      carregando: true,
      arquivosPdf: [] as IArquivoPdf[],
    });

    const computedCarregando = computed({
      get: () => props.carregando,
      set: (val: boolean) => {
        emit('update:carregando', val);
      },
    });

    const computedProdutoAcabado = computed({
      get(): IOrdemProducaoProdutoAcabado[] { return props.produtoAcabado; },
      set: (etapas: IOrdemProducaoProdutoAcabado[]) => {
        emit('update:produtoAcabado', etapas);
      },
    });

    const computedCriarApontamento = computed({
      get: () => props.criarApontamento,
      set: (criar: boolean) => {
        emit('update:criarApontamento', criar);
      },
    });

    const totalQuantidade = computed(() => {
      if (!computedProdutoAcabado.value) {
        return 0;
      }
      return computedProdutoAcabado.value.reduce((total, item) => total + (item.quantidade || 0), 0);
    });

    const totalPeso = computed(() => {
      if (!computedProdutoAcabado.value) {
        return 0;
      }
      return computedProdutoAcabado.value.reduce((total, item) => total + (item.peso || 0), 0);
    });

    function excluir(index: number) {
      computedProdutoAcabado.value.splice(index, 1);
    }

    function validarExclusao(index: number) {
      const itemParaExcluir = computedProdutoAcabado.value[index];
      if (itemParaExcluir.codigo > 0) {
        Modal.confirm({
          title: 'Confirmação de Exclusão',
          content: 'Deseja realmente excluir este produto acabado?',
          okText: 'Sim',
          cancelText: 'Não',
          onOk() {
            excluir(index);
          },
        });
      } else {
        excluir(index);
      }
    }

    function editar(index: number) {
      state.codigoProdutoSelecionado = index;
    }

    async function obterCodigoEstoque(index: number) {
      state.estoque = {} as IEstoque;
      state.estoque = await servicoEstoque.obter(computedProdutoAcabado.value[index].codigoEstoque, props.empresas[0]);
      state.estoqueItem = await servicoEstoque.obterItemEstoqueInformacoesProduto(computedProdutoAcabado.value[index].codigoEstoque, computedProdutoAcabado.value[index].codigoProduto, computedProdutoAcabado.value[index].codigoUnidade, props.empresas[0]);
      if (!state.estoqueItem) {
        Modal.confirm({
          title: 'Item de Estoque Inválido',
          content: 'Não existe vínculo do estoque selecionado com o produto informado',
          okText: 'OK',
          onOk() {
            computedProdutoAcabado.value[index].codigoEstoque = 0;
            computedProdutoAcabado.value[index].estoqueItem = 0;
          },
        });
      }
    }

    async function salvarApontamento(index: number) {
      await obterCodigoEstoque(index);
      const produtoAcabado = computedProdutoAcabado.value[index];

      if (!produtoAcabado.apontamentosProdutoAcabado || produtoAcabado.apontamentosProdutoAcabado.length === 0) {
        produtoAcabado.apontamentosProdutoAcabado = [{} as IApontamentoProdutoAcabado];
      }

      const apontamento = produtoAcabado.apontamentosProdutoAcabado[0];

      if (!apontamento.apontamentoProdutoAcabadoEstoque) {
        apontamento.apontamentoProdutoAcabadoEstoque = [];
      }

      if (apontamento.apontamentoProdutoAcabadoEstoque.length === 0) {
        apontamento.apontamentoProdutoAcabadoEstoque.push({} as IApontamentoProdutoAcabadoEstoque);
      }

      apontamento.codigoOPProdutoAcabado = produtoAcabado.codigo;
      apontamento.codigoProduto = produtoAcabado.codigoProduto;
      apontamento.codigoUnidade = produtoAcabado.codigoUnidade;
      apontamento.quantidade = produtoAcabado.quantidade;
      apontamento.codigoCentroCusto = state.estoque.codigoCentroCustoPadrao;
      apontamento.codigoEmpresa = state.estoqueItem.codigoEmpresa;
      apontamento.codigoEstoque = state.estoqueItem.codigoEstoque;
      apontamento.codigoEstoqueItem = state.estoqueItem.codigo;

      computedProdutoAcabado.value[index].estoqueItem = state.estoqueItem.codigo;
      state.carregando = false;
    }

    async function criarApontamentos() {
      for (let i = 0; i < computedProdutoAcabado.value.length; i += 1) {
        if (computedProdutoAcabado.value[i].apontamentosProdutoAcabado.length === 0) {
          if (UtilitarioGeral.validaNumerico(computedProdutoAcabado.value[i].codigoEstoque) && computedProdutoAcabado.value[i].codigoEstoque > 0) {
            await salvarApontamento(i);
          }
        }
      }
    }

    function calculaPeso(index: number) {
      const item = computedProdutoAcabado.value[index];
      if (item) {
        item.peso = (item.quantidade || 0) * (props.densidade || 0);
      }
    }

    function calculaCustoTotal(index: number) {
      computedProdutoAcabado.value[index].custoTotal = computedProdutoAcabado.value[index].custoUnitario * computedProdutoAcabado.value[index].quantidade;
      calculaPeso(index);
    }

    function handlePesoChange(index: number) {
      const item = computedProdutoAcabado.value[index];
      if (item && props.densidade > 0) {
        item.quantidade = (item.peso || 0) / props.densidade;
        item.custoTotal = item.custoUnitario * item.quantidade;
      }
    }

    async function limparUnidade(index: number) {
      const item = computedProdutoAcabado.value[index];
      item.codigoUnidade = 0;
      item.siglaUnidade = '';
      item.casasDecimais = 0;
      const definicaoProduto = await servicoProduto.obterDadosDetalhesDefinicao(item.codigoProduto);
      if (definicaoProduto) {
        item.tipoRastreabilidade = definicaoProduto.tipoRastreabilidade;
        item.tipoLancamentoRastreabilidade = definicaoProduto.tipoLancamentoRastreabilidade;
        item.casasDecimais = definicaoProduto.casasDecimais;
        item.codigoUnidade = definicaoProduto.codigoUnidade;
      }
    }

    async function adicionarProdutoAcabadoPadrao() {
      const novoProdutoAcabado: IOrdemProducaoProdutoAcabado = {
        codigo: 0,
        codigoetapa: props.codigoEtapa,
        codigoProduto: props.codigoProdutoOrdemProducao || 0,
        codigoUnidade: 0,
        quantidade: 0,
        descricao: '',
        siglaUnidade: '',
        custoUnitario: 0,
        custoTotal: 0,
        peso: 0,
        casasDecimais: 0,
        codigoEstoque: 0,
        apontamentosProdutoAcabado: [],
        estoqueItem: 0,
        ordem: 0,
        tipoRastreabilidade: ETipoRastreabilidade.Nenhum,
        tipoLancamentoRastreabilidade: ETipoLancamentoRastreabilidade.Manual,
        nomeEstoque: '',
      };
      const itemEstoque = await servicoEstoque.obterItemEstoqueDefinicaoProduto(novoProdutoAcabado.codigoProduto, true);
      novoProdutoAcabado.estoqueItem = itemEstoque ? itemEstoque.codigo : 0;
      novoProdutoAcabado.codigoEstoque = itemEstoque ? itemEstoque.codigoEstoque : 0;
      computedProdutoAcabado.value.push(novoProdutoAcabado);
      await limparUnidade(computedProdutoAcabado.value.length - 1);
    }

    async function imprimirEtiqueta(item: IOrdemProducaoProdutoAcabado) {
      const retornoRelatorio = await servicoProducao.imprimirEtiqueta(item.codigo, props.empresas[0], ETipoEtiqueta.EtiquetaIdentificacaoProducaoPA);
      if (retornoRelatorio.status === EStatusRetornoRequisicao.Sucesso) {
        state.arquivosPdf = [{ nome: '', link: retornoRelatorio.link } as IArquivoPdf];
      }
    }

    onBeforeMount(async () => {
      for (let i = 0; i < computedProdutoAcabado.value.length; i += 1) {
        calculaCustoTotal(i);
      }
      if (state.carregando) {
        await criarApontamentos();
      }
    });

    watch(() => props.criarApontamento, () => {
      if (props.criarApontamento) {
        criarApontamentos();
        computedCriarApontamento.value = false;
      }
    });

    watch(() => props.densidade, () => {
      if (computedProdutoAcabado.value && computedProdutoAcabado.value.length > 0) {
        for (let i = 0; i < computedProdutoAcabado.value.length; i += 1) {
          calculaPeso(i);
        }
      }
    });

    return {
      props,
      state,
      EPermissaoDados,
      ECustomRenderRow,
      editar,
      excluir,
      validarExclusao,
      computedCarregando,
      salvarApontamento,
      computedProdutoAcabado,
      UtilitarioGeral,
      UtilitarioMascara,
      calculaCustoTotal,
      calculaPeso,
      criarApontamentos,
      handlePesoChange,
      adicionarProdutoAcabadoPadrao,
      limparUnidade,
      imprimirEtiqueta,
      totalQuantidade,
      totalPeso,
    };
  },
});
